import React, { useState } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Grid,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { Refresh, Search, FilterList, LocationOn, Phone, Person } from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListGuestUsersQuery,
} from '../../features/admin/guestOrderlist.ts';

interface GuestUser {
  id: number;
  name: string;
  street_address: string;
  city: string;
  postal_code: string;
  country: string;
  phone: string;
  latitude: string;
  longitude: string;
  created_at: string;
}

const GuestOrderList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error'
  });

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListGuestUsersQuery({});

  // Handle API response structure
  const guestUsers = data?.data || [];
  
  const usersPerPage = 10;
  const totalRecords = guestUsers.length;
  const totalPages = Math.ceil(totalRecords / usersPerPage);

  // Filter users based on search
  const filteredUsers = guestUsers.filter((user: GuestUser) => 
    user.name?.toLowerCase().includes(searchStr.toLowerCase()) ||
    user.phone?.includes(searchStr) ||
    user.city?.toLowerCase().includes(searchStr.toLowerCase()) ||
    user.country?.toLowerCase().includes(searchStr.toLowerCase())
  );

  // Paginate users
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * usersPerPage,
    page * usersPerPage
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Guest users refreshed successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error refreshing guest user',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setSearchStr('');
    setPage(1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for desktop */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        {/* Main content area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Guest Users
                </Typography>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#c9cccf',
                    color: '#202223',
                    borderRadius: '8px',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#202223',
                      bgcolor: 'transparent',
                    }
                  }}
                >
                  Refresh
                </Button>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(e, v) => setTabValue(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#202223',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label={`All (${totalRecords})`} />
                   
                  </Tabs>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search guest users..."
                    value={searchStr}
                    onChange={handleSearch}
                    fullWidth
                    sx={{ 
                      maxWidth: 400,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Box>

                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">Failed to load guest users. Please try again.</Alert>
                  </Box>
                )}

                {!isLoading && !isError && paginatedUsers.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No guest users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {searchStr ? `No results for "${searchStr}"` : 'Guest users will appear here when they place orders'}
                    </Typography>
                    {searchStr && (
                      <Button
                        variant="outlined"
                        onClick={clearFilters}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#c9cccf',
                          color: '#202223',
                          borderRadius: '8px',
                          fontWeight: 500,
                        }}
                      >
                        Clear search
                      </Button>
                    )}
                  </Box>
                )}

                {!isLoading && paginatedUsers.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>
                    {/* Table Header - Desktop */}
                    {!isMobile && (
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 1fr 200px 120px 120px 140px 120px 120px 180px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          #
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          User
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Address
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          City
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Postal Code
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Country
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Phone
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Coordinates
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Created
                        </Typography>
                      </Box>
                    )}

                    {/* Table Rows */}
                    {paginatedUsers.map((user: GuestUser, index: number) => (
                      <Box
                        key={user.id}
                        sx={!isMobile ? {
                          display: 'grid',
                          gridTemplateColumns: '50px 1fr 200px 120px 120px 140px 120px 120px 180px',
                          gap: 2,
                          px: 2,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: index < paginatedUsers.length - 1 ? '1px solid #e1e3e5' : 'none',
                          '&:hover': { bgcolor: '#f9fafb' },
                          transition: 'background-color 0.15s ease',
                        } : {
                          p: 2,
                          borderBottom: index < paginatedUsers.length - 1 ? '1px solid #e1e3e5' : 'none',
                          '&:hover': { bgcolor: '#f9fafb' },
                        }}
                      >
                        {!isMobile ? (
                          <>
                            <Typography variant="body2" sx={{ color: '#6d7175' }}>
                              {(page - 1) * usersPerPage + index + 1}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                sx={{ 
                                  width: 32, 
                                  height: 32, 
                                  bgcolor: '#f1f2f4',
                                  color: '#202223',
                                  fontSize: '14px',
                                  fontWeight: 600
                                }}
                              >
                                {(user.name || 'U')[0].toUpperCase()}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                {user.name || 'Unknown User'}
                              </Typography>
                            </Box>

                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{
                                fontSize: '13px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={user.street_address}
                            >
                              {user.street_address || '-'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              {user.city || '-'}
                            </Typography>

                            <Chip 
                              label={user.postal_code || '-'} 
                              size="small"
                              sx={{ 
                                bgcolor: '#f1f2f4',
                                color: '#202223',
                                fontWeight: 500,
                                fontSize: '11px'
                              }}
                            />

                            <Typography variant="body2" color="text.secondary">
                              {user.country || '-'}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Phone sx={{ fontSize: 14, color: '#6d7175' }} />
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                {user.phone || '-'}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn sx={{ fontSize: 14, color: '#6d7175' }} />
                              <Typography variant="body2" sx={{ fontSize: '11px', color: '#6d7175' }}>
                                {user.latitude && user.longitude ? 'Yes' : 'No'}
                              </Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                              {formatDate(user.created_at)}
                            </Typography>
                          </>
                        ) : (
                          // Mobile View
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                  sx={{ 
                                    width: 40, 
                                    height: 40, 
                                    bgcolor: '#f1f2f4',
                                    color: '#202223',
                                    fontWeight: 600
                                  }}
                                >
                                  {(user.name || 'U')[0].toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                    {user.name || 'Unknown User'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <Phone sx={{ fontSize: 14, color: '#6d7175' }} />
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                      {user.phone || '-'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                #{(page - 1) * usersPerPage + index + 1}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Address:
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '14px' }}>
                                {user.street_address || 'No address provided'}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip 
                                label={user.city || 'No city'} 
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '11px' }}
                              />
                              <Chip 
                                label={`Postal: ${user.postal_code || 'N/A'}`} 
                                size="small"
                                sx={{ 
                                  bgcolor: '#f1f2f4',
                                  color: '#202223',
                                  fontWeight: 500,
                                  fontSize: '11px'
                                }}
                              />
                              <Chip 
                                label={user.country || 'No country'} 
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '11px' }}
                              />
                            </Box>

                            {user.latitude && user.longitude && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOn sx={{ fontSize: 16, color: '#008060' }} />
                                <Typography variant="caption" color="text.secondary">
                                  Coordinates available
                                </Typography>
                              </Box>
                            )}

                            <Typography variant="caption" color="text.secondary">
                              Created: {formatDate(user.created_at)}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Card>

              {totalPages > 1 && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handleChangePage}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: '8px',
                        fontWeight: 500,
                      },
                      '& .Mui-selected': {
                        bgcolor: '#202223 !important',
                        color: 'white',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GuestOrderList;